import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Icon, Button, Badge, Card, PageHeader, ListItem, SectionHeader,
  CheckBox, RadioButton,
  GuestInfoSection,
  StickyBottomBar, PriceDisplay, Spinner,
} from '../../../components/common';
import VehicleInfoSection from '../../../components/service/VehicleInfoSection';
import PaymentAgreementCard from '../../../components/service/PaymentAgreementCard';
import { makeServiceLabels } from '../../../constants/serviceLabels';
import { paymentMethods as defaultPaymentMethods } from '../../../constants/uiConstants';
import EasyPayProviderModal from '../../../components/service/EasyPayProviderModal';
import { reservationService, pricingService } from '../../../api/services';
import { executePayment, isPortOneReady } from '../../../api/portone';
import { useMoney } from '../../../hooks/useMoney';
import { translateApiError, appendErrorReason } from '../../../utils/apiError';
import useAlertModal from '../../../hooks/useAlertModal';

export default function SurchargePayment() {
  const { t } = useTranslation();
  const money = useMoney();
  const serviceLabels = makeServiceLabels(t);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(location.state?.surcharge || null);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState(null);
  const [vehicleData, setVehicleData] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [terms, setTerms] = useState([]);

  useEffect(() => {
    if (data) return;
    reservationService.getById(id)
      .then((res) => {
        setData({
          service_type: res.service_type || 'airport',
          scheduled_date: res.scheduled_date || (res.reservation_date ? `${res.reservation_date} ${res.start_time || ''}`.trim() : ''),
          vehicle: res.vehicle,
          customer_name: res.booker_name || res.customer_name,
          phone: res.booker_phone || res.phone,
          email: res.booker_email || res.email,
          charges: res.additional_charges || [],
          total_amount: res.additional_total || 0,
        });
      })
      .catch((err) => setError(translateApiError(err, t, 'errors.generic')))
      .finally(() => setLoading(false));
  }, [data, id, t]);

  useEffect(() => {
    const svcType = data?.service_type || 'airport';
    pricingService.getVehicleInfo(svcType).then(setVehicleData).catch((e) => console.warn(e.message));
    pricingService.getPaymentTerms().then((list) => setTerms(Array.isArray(list) ? list : [])).catch(() => setTerms([]));
    setPaymentMethods(defaultPaymentMethods);
  }, [data?.service_type]);

  // 기본 선택은 card. simple(간편결제)은 2026-06-09 재가맹으로 옵션 복원됐으나 기본값은 card 유지.
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [easyPayOpen, setEasyPayOpen] = useState(false);
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeRequired, setAgreeRequired] = useState(false);
  const [agreeOptional, setAgreeOptional] = useState(false);

  const handleAgreeAll = () => {
    const next = !agreeAll;
    setAgreeAll(next);
    setAgreeRequired(next);
    setAgreeOptional(next);
  };

  const [paying, setPaying] = useState(false);
  const { showAlert, alertModal } = useAlertModal();

  const handlePayment = async (methodOverride) => {
    if (!isPortOneReady()) {
      console.error('[Payment] PortOne 미준비 — IMP_CODE/CHANNEL_KEY/SDK 확인 필요');
      showAlert(t('payment.alert_payment_module_not_ready'));
      return;
    }
    // 간편결제(simple): 결제수단 선택 모달을 띄우고, 모달에서 고른 provider 로 재호출.
    const method = (typeof methodOverride === 'string' ? methodOverride : null) || paymentMethod;
    if (method === 'simple') { setEasyPayOpen(true); return; }

    setPaying(true);
    try {
      const result = await executePayment({
        reservationId: Number(id),
        paymentType: 'additional',
        paymentMethod: method,
        buyerName: data.customer_name,
        buyerTel: data.phone,
        buyerEmail: data.email,
        redirectUrl: `${window.location.origin}/reservation/${id}`,
      });
      if (result?.status === 'ready') {
        alert(`${t('complete.vbank_info')}\n${t('complete.vbank_bank')}: ${result.vbank_name}\n${t('complete.vbank_num')}: ${result.vbank_num}\n${t('complete.vbank_holder')}: ${result.vbank_holder}\n${t('complete.vbank_notice')}`);
      }
      navigate(`/reservation/${id}`);
    } catch (err) {
      // PG/서버가 내려준 사유(err.message)를 base 뒤에 "(사유: XXX)" 형태로 부착.
      const cancelled = err?.code === 'payment_cancelled' || err?.code === 'payment_not_completed';
      const base = translateApiError(err, t, cancelled ? 'payment.alert_payment_cancelled' : 'payment.alert_payment_failed');
      showAlert(
        appendErrorReason(base, err, t),
        { variant: cancelled ? 'info' : 'error' },
      );
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <><Navbar /><div className="flex justify-center py-8"><Spinner /></div></>;
  if (error) return <><Navbar /><p className="text-sm text-red-500 text-center py-8">{error}</p></>;
  if (!data) return <><Navbar /><p className="text-sm text-mono-400 text-center py-8">{t('complete.no_reservation_data')}</p></>;

  const serviceType = data.service_type || data.serviceType || 'airport';
  const v = vehicleData;
  const charges = data.charges || [];
  const totalAmount = data.total_amount || 0;

  return (
    <>
      <Navbar />
      <main className="bg-mono-50 min-h-screen pb-36 sm:pb-32">
        <div className="max-w-form mx-auto px-2.5 sm:px-5 py-5 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header: Back + Title ===== */}
          <PageHeader title={t('reservation.surcharge_title')} />

          {/* ===== Service info ===== */}
          <Card>
            <div className="space-y-2">
              <Badge variant="service" serviceType="transfer">{t('nav.private_transfer')}</Badge>
              <h2 className="text-base font-bold text-mono-900">{serviceLabels[serviceType] || serviceLabels.airport}</h2>
              <p className="text-2sm text-mono-500">{t('payment.scheduled_use_time')}: {data.scheduled_date || data.dateTime}</p>
            </div>
          </Card>

          {/* ===== 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('complete.vehicle_info')} />
            <VehicleInfoSection
              images={v.images}
              carName={data.vehicle?.name || v.carName}
              usageTimeBadge={v.usageTimeBadge}
              specs={[
                { icon: 'van', text: v.seat },
                { icon: 'person', text: v.capacity },
                { icon: 'bag', text: v.luggage },
              ]}
              waitInfo={v.waitInfo}
              showReview={false}
              showPriceNote={false}
            />
          </Card>

          {/* ===== 예약자 정보 ===== */}
          <Card>
            <GuestInfoSection
              name={data.customer_name || data.reserverName}
              phone={data.phone}
              email={data.email}
            />
          </Card>

          {/* ===== 추가 요금 내역 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('reservation.surcharge_title')} />
            {charges.map((charge, i) => (
              <ListItem key={i} label={charge.label} value={`${money(charge.amount)}`} layout="inline" />
            ))}
            <div className="flex items-center justify-between pt-4 mt-4 border-t border-mono-200">
              <span className="text-base font-bold text-mono-900">{t('reservation.surcharge_amount')}</span>
              <p className="flex items-baseline gap-0.5">
                <span className="text-2xl font-bold text-mono-900">{money(totalAmount)}{/^US\$/.test(String(money(totalAmount))) ? ' ~' : ''}</span>
              </p>
            </div>
          </Card>

          {/* ===== 결제 수단 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('payment.method')} />
            <div className="space-y-3">
              {paymentMethods.map((m) => (
                <RadioButton
                  key={m.id}
                  name="payment-method"
                  label={m.labelKey ? t(m.labelKey) : m.label}
                  value={m.id}
                  checked={paymentMethod === m.id}
                  onChange={() => setPaymentMethod(m.id)}
                />
              ))}
            </div>
          </Card>

          {/* ===== 약관 동의 ===== */}
          <PaymentAgreementCard onAllAgreedChange={(v) => setAgreeAll(v)} />

        </div>
      </main>

      {/* ===== Sticky bottom bar ===== */}
      <StickyBottomBar>
        <PriceDisplay label={t('reservation.surcharge_amount')} amount={totalAmount} approx />
        <Button onClick={handlePayment} disabled={!agreeAll || paying} width="responsive-130">{paying ? t('payment.paying') : t('payment.pay_button')}</Button>
      </StickyBottomBar>
      {alertModal}
      <EasyPayProviderModal
        open={easyPayOpen}
        onClose={() => setEasyPayOpen(false)}
        onSelect={(providerId) => { setEasyPayOpen(false); handlePayment(providerId); }}
      />
    </>
  );
}
